import { createPortal } from "react-dom";
import clsx from "clsx";
import { memo, useEffect, useRef, useState } from "react";

import classes from "./styles.module.scss";

import type { IModalProps } from "@components/atoms/modal/types";
import { getContainerStyle } from "@components/atoms/modal/config";
import { Box } from "@components/atoms/box";
import { DragContainer } from "@components/atoms/dragContainer";
import { blockScroll, restoreScroll } from "@/utils/scroll";
import { clearModalOptions } from "@components/modals/baseModal/store";

const container: HTMLElement = document.querySelector("#modalRoot")!;

/**
 * Modal component that can be used to display pop-up modal dialogs.
 *
 * @param {IModalProps} props - The props for the Modal component.
 * @param {boolean} props.open - Indicates whether the modal is open or not.
 * @param {Function} [props.onClose] - A callback function to be called when the modal is closed.
 * @param {number|string} [props.maxWidth] - The maximum width of the modal. It can be a number or a CSS string (e.g., '400px').
 * @param {boolean} [props.notScroll=false] - If `true`, prevents scrolling of the background content when the modal is open.
 * @param {string} [props.bodyClassname] - Additional CSS class to apply to the modal body.
 * @param {boolean} [props.backdrop=true] - If `true`, displays a backdrop behind the modal.
 * @param {boolean} [props.draggable=false] - If `true`, allows dragging of the modal by the user.
 * @param {React.ReactNode} props.children - The content to be displayed within the modal.
 * @returns {React.ReactPortal} The React portal containing the modal.
 */
export const Modal = memo<IModalProps>((props) => {
  const {
    open,
    onClose,
    maxWidth,
    notScroll = false,
    bodyClassname,
    backdrop = true,
    draggable = false,
    children,
    minWidth,
  } = props;

  const nodeRef = useRef<HTMLDivElement>(null);

  const [localOpen, setLocalOpen] = useState<boolean>(open);
  const [mouseDownInside, setMouseDownInside] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      getContainerStyle(container.style, false);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setLocalOpen(open);
    } else if (localOpen) {
      setTimeout(() => {
        setLocalOpen(open);
        clearModalOptions();
      }, 200);
    } else {
      setLocalOpen(open);
    }

    if (!notScroll) {
      if (open) {
        blockScroll();
      } else {
        restoreScroll();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notScroll, open]);

  useEffect(() => {
    getContainerStyle(container.style, localOpen);
  }, [localOpen]);

  /*  useEffect(() => {
    if (!open&&localOpen){

    }
  }, [open]);*/

  useEffect(() => {
    return () => {
      restoreScroll();
    };
  }, []);

  const handleBackdropClick = () => {
    if (!mouseDownInside && onClose) {
      onClose();
    }
  };

  const handleModalMouseDown = () => {
    setMouseDownInside(true);
  };

  const handleModalMouseUp = () => {
    setMouseDownInside(false);
  };

  const modalBody = (
    <div
      className={clsx(
        classes.modalContainer,
        backdrop && classes.backdrop,
        !(open || localOpen) && classes.displayNone,
      )}
      onClick={handleBackdropClick}
    >
      {(open || localOpen) && (
        <DragContainer
          disabled={!draggable}
          nodeRef={nodeRef}
          className={classes.dragContainer}
        >
          <Box
            ref={nodeRef}
            radius={"lg"}
            px={"0"}
            py={"0"}
            shadow
            className={clsx(
              classes.anim,
              !open && classes.exitAnim,
              classes.radius,
            )}
            borderNone
          >
            <div
              onMouseDown={handleModalMouseDown}
              onMouseUp={handleModalMouseUp}
              onClick={(event) => {
                event.stopPropagation();
              }}
              style={{ maxWidth, minWidth }}
              className={clsx(classes.modal, bodyClassname)}
            >
              <div className={clsx(classes.scrollContainer)}>{children}</div>
            </div>
          </Box>
        </DragContainer>
      )}
    </div>
  );

  return createPortal(modalBody, container);
});

Modal.displayName = "Modal";
